import { ApiResponse, getFromServer, postToServer, BASE_URL } from "server";
import {
  CreateExplanationRequestInput,
  ProposeTimeSlotsInput,
  SelectTimeSlotInput,
  AssignTeacherInput,
  RejectRequestInput,
  UpdateRequestStatusInput,
  GetRequestsFiltersInput,
} from "../InputsTypes";
import {
  CreateExplanationRequestOutput,
  ProposeTimeSlotsOutput,
  SelectTimeSlotOutput,
  AssignTeacherOutput,
  RejectRequestOutput,
  GetRequestsOutput,
  GetRequestDetailsOutput,
  GetExplanationStatsOutput,
  GetAvailableTeachersOutput,
  GetSubjectsOutput,
  GetStudentClassesOutput,
  GetTeacherAvailabilityOutput,
} from "../OutputsTypes";

// Route : POST /explanations/request/create
export const createExplanationRequest = async (
  input: CreateExplanationRequestInput,
): Promise<ApiResponse<CreateExplanationRequestOutput>> => {
  const formData = new FormData();
  formData.append("studentClass", input.studentClass);
  formData.append("studentEmail", input.studentEmail);
  formData.append("subject", input.subject);
  formData.append("description", input.description);

  // Ajouter les fichiers si présents
  if (input.files && input.files.length > 0) {
    input.files.forEach((file, index) => {
      formData.append(`files`, file, file.name);
    });
  }

  return await postToServer<FormData, CreateExplanationRequestOutput>(
    "/explanations/request/create",
    formData,
  );
};

// Route : GET /explanations/requests/my
export const getMyExplanationRequests = async (): Promise<
  ApiResponse<GetRequestsOutput>
> => {
  return await getFromServer<GetRequestsOutput>("/explanations/requests/my");
};

// Route : GET /explanations/requests/assigned
export const getAssignedExplanationRequests = async (): Promise<
  ApiResponse<GetRequestsOutput>
> => {
  return await getFromServer<GetRequestsOutput>(
    "/explanations/requests/assigned",
  );
};

// Route : GET /explanations/requests/pending
export const getPendingExplanationRequests = async (
  filters?: GetRequestsFiltersInput,
): Promise<ApiResponse<GetRequestsOutput>> => {
  const queryParams = new URLSearchParams();

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value.toString());
      }
    });
  }

  const url = queryParams.toString()
    ? `/explanations/requests/pending?${queryParams.toString()}`
    : "/explanations/requests/pending";

  return await getFromServer<GetRequestsOutput>(url);
};

// Route : GET /explanations/request/:id
export const getExplanationRequestDetails = async (
  requestId: string,
): Promise<ApiResponse<GetRequestDetailsOutput>> => {
  return await getFromServer<GetRequestDetailsOutput>(
    `/explanations/request/${requestId}`,
  );
};

// Route : POST /explanations/request/:id/propose-slots
export const proposeTimeSlots = async (
  input: ProposeTimeSlotsInput,
): Promise<ApiResponse<ProposeTimeSlotsOutput>> => {
  return await postToServer<ProposeTimeSlotsInput, ProposeTimeSlotsOutput>(
    `/explanations/request/${input.requestId}/propose-slots`,
    input,
  );
};

// Route : POST /explanations/request/:id/select-slot
export const selectTimeSlot = async (
  input: SelectTimeSlotInput,
): Promise<ApiResponse<SelectTimeSlotOutput>> => {
  return await postToServer<SelectTimeSlotInput, SelectTimeSlotOutput>(
    `/explanations/request/${input.requestId}/select-slot`,
    input,
  );
};

// Fonction wrapper pour selectTimeSlot avec paramètres séparés
export const selectTimeSlotById = async (
  requestId: string,
  slotId: string,
): Promise<ApiResponse<SelectTimeSlotOutput>> => {
  return await selectTimeSlot({
    requestId,
    slotId,
  });
};

// Route : POST /explanations/request/:id/assign-teacher
export const assignTeacher = async (
  input: AssignTeacherInput,
): Promise<ApiResponse<AssignTeacherOutput>> => {
  return await postToServer<AssignTeacherInput, AssignTeacherOutput>(
    `/explanations/request/${input.requestId}/assign-teacher`,
    input,
  );
};

// Fonction wrapper pour assignTeacher avec paramètres séparés
export const assignTeacherById = async (
  requestId: string,
  teacherId: string,
  adminNotes?: string,
): Promise<ApiResponse<AssignTeacherOutput>> => {
  return await assignTeacher({
    requestId,
    teacherId,
    adminNotes,
  });
};

// Route : POST /explanations/request/:id/reject
export const rejectRequest = async (
  input: RejectRequestInput,
): Promise<ApiResponse<RejectRequestOutput>> => {
  return await postToServer<RejectRequestInput, RejectRequestOutput>(
    `/explanations/request/${input.requestId}/reject`,
    input,
  );
};

// Fonction wrapper pour rejectRequest avec paramètres séparés
export const rejectRequestById = async (
  requestId: string,
  reason: string,
  adminNotes?: string,
): Promise<ApiResponse<RejectRequestOutput>> => {
  return await rejectRequest({
    requestId,
    reason,
    adminNotes,
  });
};

// Route : POST /explanations/request/:id/update-status
export const updateRequestStatus = async (
  input: UpdateRequestStatusInput,
): Promise<ApiResponse<{ message: string; success: boolean }>> => {
  return await postToServer<
    UpdateRequestStatusInput,
    { message: string; success: boolean }
  >(`/explanations/request/${input.requestId}/update-status`, input);
};

// Route : GET /explanations/stats
export const getExplanationStats = async (): Promise<
  ApiResponse<GetExplanationStatsOutput>
> => {
  return await getFromServer<GetExplanationStatsOutput>("/explanations/stats");
};

// Route : GET /explanations/teachers/available
export const getAvailableTeachers = async (): Promise<
  ApiResponse<GetAvailableTeachersOutput>
> => {
  return await getFromServer<GetAvailableTeachersOutput>(
    "/explanations/teachers/available",
  );
};

// Route : GET /explanations/subjects
export const getSubjects = async (): Promise<
  ApiResponse<GetSubjectsOutput>
> => {
  return await getFromServer<GetSubjectsOutput>("/explanations/subjects");
};

// Route : GET /explanations/classes
export const getStudentClasses = async (): Promise<
  ApiResponse<GetStudentClassesOutput>
> => {
  return await getFromServer<GetStudentClassesOutput>("/explanations/classes");
};

// Route : GET /explanations/teacher/availability
export const getTeacherAvailability = async (): Promise<
  ApiResponse<GetTeacherAvailabilityOutput>
> => {
  return await getFromServer<GetTeacherAvailabilityOutput>(
    "/explanations/teacher/availability",
  );
};

// Route : POST /explanations/teacher/availability/update
export const updateTeacherAvailability = async (
  slots: { startTime: Date; endTime: Date }[],
): Promise<ApiResponse<{ message: string; success: boolean }>> => {
  return await postToServer<
    { slots: { startTime: Date; endTime: Date }[] },
    { message: string; success: boolean }
  >("/explanations/teacher/availability/update", { slots });
};

// Route : GET /explanations/notifications
export const getExplanationNotifications = async (): Promise<
  ApiResponse<{
    notifications: {
      _id: string;
      type: string;
      title: string;
      message: string;
      requestId?: string;
      isRead: boolean;
      createdAt: string;
    }[];
    unreadCount: number;
  }>
> => {
  return await getFromServer<{
    notifications: {
      _id: string;
      type: string;
      title: string;
      message: string;
      requestId?: string;
      isRead: boolean;
      createdAt: string;
    }[];
    unreadCount: number;
  }>("/explanations/notifications");
};

// Route : POST /explanations/notifications/:id/mark-read
export const markNotificationAsRead = async (
  notificationId: string,
): Promise<ApiResponse<{ message: string; success: boolean }>> => {
  return await postToServer<{}, { message: string; success: boolean }>(
    `/explanations/notifications/${notificationId}/mark-read`,
    {},
  );
};
